import { useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/telemetry';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import type { DealPriority, NextStep } from '@/types';
import { NEXT_STEP_LABELS } from '@/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Props {
  trigger?: ReactNode;
}

const PRIORITIES: { value: DealPriority; label: string }[] = [
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cold', label: 'Cold' },
];

// Available next-steps when creating a deal (sold/delivery only apply later).
const NEW_DEAL_NEXT_STEPS: NextStep[] = [
  'call', 'follow_up', 'appointment', 'send_numbers', 'trade_review', 'close',
];

export function NewDealDialog({ trigger }: Props) {
  const navigate = useNavigate();
  const { mode, userId, dealershipId } = useAuth();
  const { refresh } = useDealershipData();
  const { hasAccess, loading: subLoading } = useSubscription();

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [phone, setPhone] = useState('');
  const [estValue, setEstValue] = useState('');
  const [priority, setPriority] = useState<DealPriority>('warm');
  const [notes, setNotes] = useState('');
  const [nextStep, setNextStep] = useState<NextStep | ''>('');

  const reset = () => {
    setCustomerName('');
    setVehicle('');
    setPhone('');
    setEstValue('');
    setPriority('warm');
    setNotes('');
    setNextStep('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'real' && !subLoading && !hasAccess) {
      toast.error('Your trial has ended', {
        description: 'Start a plan to keep creating deals.',
        action: { label: 'See plans', onClick: () => { setOpen(false); navigate('/pricing'); } },
      });
      return;
    }
    if (!customerName.trim() || !vehicle.trim()) {
      toast.error('Customer name and vehicle are required');
      return;
    }
    if (!nextStep) {
      toast.error('Pick a next step — every deal needs one.');
      return;
    }

    if (mode === 'demo') {
      toast.info('Demo mode — sign in to a real dealership to save deals');
      setOpen(false);
      reset();
      return;
    }

    if (!dealershipId || !userId) {
      toast.error('You must be signed in to a dealership to create a deal');
      return;
    }

    setSubmitting(true);
    const { data, error } = await supabase
      .from('deals')
      .insert({
        dealership_id: dealershipId,
        rep_id: userId,
        customer_name: customerName.trim(),
        vehicle: vehicle.trim(),
        phone: phone.trim() || null,
        estimated_value: estValue ? Number(estValue) : 0,
        priority,
        stage: 'new',
        status: 'active',
        next_step: nextStep,
        notes: notes.trim(),
      })
      .select('id')
      .maybeSingle();
    setSubmitting(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success('Deal created');
    track('deal_created', { userId, dealershipId, mode }, { priority, has_next_step: true });
    refresh();
    setOpen(false);
    reset();
    if (data?.id) navigate(`/deals/${data.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="lg" variant="outline" className="w-full text-base h-14 gap-2">
            <Plus className="h-5 w-5" /> New Deal
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Deal</DialogTitle>
          <DialogDescription>Capture the basics. You can flesh it out later.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Customer name *</label>
            <Input
              autoFocus
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder="Sarah Thompson"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Vehicle *</label>
            <Input
              value={vehicle}
              onChange={e => setVehicle(e.target.value)}
              placeholder="2022 Toyota RAV4 XLE"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone</label>
              <Input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="(519) 555-0100"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Est. value</label>
              <Input
                type="number"
                inputMode="numeric"
                value={estValue}
                onChange={e => setEstValue(e.target.value)}
                placeholder="95000"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                    priority === p.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary text-secondary-foreground border-border hover:bg-accent'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Next step *</label>
            <Select value={nextStep} onValueChange={(v) => setNextStep(v as NextStep)}>
              <SelectTrigger>
                <SelectValue placeholder="What happens next?" />
              </SelectTrigger>
              <SelectContent>
                {NEW_DEAL_NEXT_STEPS.map(ns => (
                  <SelectItem key={ns} value={ns}>{NEXT_STEP_LABELS[ns]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Every deal needs a next step — that's how we keep momentum.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="What did the customer say?"
              rows={3}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create deal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}